import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const BurnRate = () => {
  // State variables for Burn Rate calculation
  const [startingCash, setStartingCash] = useState(0); // Starting cash balance
  const [endingCash, setEndingCash] = useState(0); // Ending cash balance
  const [months, setMonths] = useState(0); // Number of months
  const [burnRate, setBurnRate] = useState(0); // Calculated burn rate

  // Event handlers to update state values
  const handleStartingCashChange = (e) =>
    setStartingCash(parseFloat(e.target.value) || 0);
  const handleEndingCashChange = (e) =>
    setEndingCash(parseFloat(e.target.value) || 0);
  const handleMonthsChange = (e) => setMonths(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate burn rate when any input changes
  useEffect(() => {
    if (months > 0) {
      const rate = (startingCash - endingCash) / months;
      setBurnRate(rate);
    } else {
      setBurnRate(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [startingCash, endingCash, months]);

  return (
    <Layout title="Burn Rate Calculator" path="burn-rate">
      <section className="section-padding" id="burn-rate-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Burn Rate Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Burn Rate Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Starting Cash Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="startingCash" className="form-label">
                        Starting Cash Balance (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Starting Cash Balance:</strong> The initial cash balance at the beginning of the period being evaluated."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="startingCash"
                        placeholder="Enter starting cash balance"
                        value={startingCash || ""}
                        onChange={handleStartingCashChange}
                      />
                    </div>

                    {/* Ending Cash Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="endingCash" className="form-label">
                        Ending Cash Balance (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Ending Cash Balance:</strong> The cash balance at the end of the evaluation period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="endingCash"
                        placeholder="Enter ending cash balance"
                        value={endingCash || ""}
                        onChange={handleEndingCashChange}
                      />
                    </div>

                    {/* Number of Months Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="months" className="form-label">
                        Number of Months
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Months:</strong> The total time period (in months) over which the burn rate is calculated."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="months"
                        placeholder="Enter number of months"
                        value={months || ""}
                        onChange={handleMonthsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Burn Rate:{" "}
                        <span className="fw-bold ms-3">
                          {burnRate === 0
                            ? "0.00"
                            : burnRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹/month
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Burn Rate Calculator
                </h3>

                {/* What is a Burn Rate Calculator? */}
                <h4 className="mt-3 mb-3">What is a Burn Rate Calculator?</h4>
                <p>
                  The **Burn Rate Calculator** is a financial tool used by
                  startups to measure the rate at which they are spending cash
                  reserves over a given period. It helps in tracking the
                  operational sustainability and financial runway of a business.
                </p>

                {/* Why Use a Burn Rate Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Burn Rate Calculator?</h4>
                <p>
                  This calculator is crucial for startups to monitor their cash
                  flow, budget effectively, and make informed decisions about
                  scaling operations or seeking additional funding.
                </p>

                {/* Benefits of Using a Burn Rate Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Burn Rate Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Assesses Financial Runway:</strong> Helps determine
                    how long the business can operate with the current cash
                    reserves.
                  </li>
                  <li>
                    <strong>Aids in Budgeting:</strong> Provides insights into
                    monthly expenses to optimize spending.
                  </li>
                  <li>
                    <strong>Supports Fundraising Efforts:</strong> Helps convey
                    financial sustainability to potential investors.
                  </li>
                  <li>
                    <strong>Identifies Cost-Saving Opportunities:</strong>{" "}
                    Highlights areas where spending can be reduced.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Burn Rate Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Startups:</strong> To monitor and manage cash flow
                    effectively.
                  </li>
                  <li>
                    <strong>Financial Planning Teams:</strong> For creating and
                    evaluating budgets.
                  </li>
                  <li>
                    <strong>Investors:</strong> To assess the sustainability and
                    financial health of a startup.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is a good burn rate for a startup?
                </h4>
                <p>
                  A good burn rate varies depending on the industry, stage, and
                  funding. Ideally, the burn rate should align with the
                  startup's growth goals and runway, ensuring enough time to
                  achieve key milestones.
                </p>

                <h4 className="mt-4 mb-3">How can I reduce my burn rate?</h4>
                <p>
                  To reduce burn rate, prioritize essential spending,
                  renegotiate vendor contracts, minimize overhead costs, and
                  optimize operational efficiency.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is the burn rate critical for startups?
                </h4>
                <p>
                  The burn rate is critical as it directly impacts the financial
                  runway and determines how long a startup can sustain
                  operations without additional funding. Monitoring it ensures
                  proactive financial management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BurnRate;
